import React, { useState } from 'react'
import { api } from '../utils/supabase'
import { Upload, FileText, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react'
import Icon3D from '../components/Icon3D'

export default function UploadCSV() {
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState({
    trackers: null,
    toka: null,
    oui: null,
    telcel: null,
    allce: null
  })

  const handleFileSelect = (type, e) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [type]: file }))
    }
  }

  const handleUpload = async (type) => {
    const file = selectedFiles[type]
    if (!file) return

    setUploading(true)
    setResults(null)

    try {
      const formData = new FormData()
      formData.append('csv', file)
      formData.append('type', type)

      const response = await fetch('/api/sync/upload', {
        method: 'POST',
        body: formData
      })

      const data = await response.json()
      
      setResults(data)
      if (data.success) {
        setSelectedFiles(prev => ({ ...prev, [type]: null }))
      }
    } catch (err) {
      console.error('Upload error:', err)
      setResults({ error: err.message })
    } finally {
      setUploading(false)
    }
  }

  const uploadTypes = [
    { 
      id: 'trackers', 
      label: 'Trackers GPS', 
      desc: 'CSV exportado de Navixy (id, label, device_id, phone, connection_status, etc.)',
      required: ['id', 'label', 'device_id', 'phone']
    },
    { 
      id: 'toka', 
      label: 'TOKA SIM', 
      desc: 'CSV del panel TOKA (MSISDN, SIM, Estatus de Linea, Plan, etc.)',
      required: ['MSISDN', 'SIM', 'Estatus de Linea']
    },
    { 
      id: 'oui', 
      label: 'OUI (Líneas Amarillas)', 
      desc: 'CSV del panel OUI (FIMDN, FCICCID, STATUS, PLAN, etc.)',
      required: ['FIMDN', 'FCICCID', 'STATUS']
    },
    { 
      id: 'telcel', 
      label: 'Telcel B2B', 
      desc: 'CSV de recargas (telefono, carrier, monto)',
      required: ['telefono']
    },
    {
      id: 'allce',
      label: 'ALLCE',
      desc: 'CSV de ALLCE (ICCID, Fecha, Plan cargado, NUMERO)',
      required: ['ICCID', 'Plan cargado']
    }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Icon3D icon={Upload} color="emerald" size="md" /> Importar CSV
        </h1>
        <p className="text-gray-400 text-sm">Sincroniza tus archivos CSV con Supabase</p>
      </div>

      {results && (
        <div className={`card p-4 ${results.success ? 'bg-emerald-500/10 !border-emerald-500/40' : 'bg-red-500/10 !border-red-500/40'}`}>
          <div className="flex items-center gap-2">
            {results.success ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
            <span className={results.success ? 'text-emerald-400' : 'text-red-400'}>
              {results.success ? 'Sincronización exitosa' : 'Error en sincronización'}
            </span>
          </div>
          {results.processed && (
            <p className="text-sm text-gray-300 mt-2">
              Procesados: {results.processed} registros<br/>
              Insertados: {results.inserted} registros
            </p>
          )}
          {results.error && (
            <p className="text-sm text-red-400 mt-2">{results.error}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {uploadTypes.map((type) => (
          <div key={type.id} className="card card-hover p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Icon3D icon={FileText} color="amber" size="sm" />
                  {type.label}
                </h3>
                <p className="text-sm text-gray-400 mt-1">{type.desc}</p>
              </div>
              {selectedFiles[type.id] && (
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                  {selectedFiles[type.id].name}
                </span>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => handleFileSelect(type.id, e)}
                  />
                   <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl border border-dashed border-white/20 transition-colors">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-300">
                      {selectedFiles[type.id] ? 'Cambiar archivo' : 'Seleccionar CSV'}
                    </span>
                  </div>
                </label>
                
                {selectedFiles[type.id] && (
                  <button
                    onClick={() => handleUpload(type.id)}
                    disabled={uploading}
                    className="btn btn-primary"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Sync...
                      </>
                    ) : (
                      'Sincronizar'
                    )}
                  </button>
                )}
              </div>

              <div className="text-xs text-gray-500">
                Columnas requeridas: {type.required.join(', ')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sync All Button */}
      <div className="card p-6">
        <h3 className="font-semibold text-white mb-2">Sincronización Manual</h3>
        <p className="text-sm text-gray-400 mb-4">
          Ejecuta la sincronización usando los archivos configurados en .env
        </p>
        <button
          onClick={async () => {
            setUploading(true)
            try {
              await api.post('/api/sync/manual', {})
              setResults({ success: true, message: 'Sincronización manual completada' })
            } catch (err) {
              setResults({ success: false, error: err.message })
            } finally {
              setUploading(false)
            }
          }}
          disabled={uploading}
          className="btn btn-ghost w-full"
        >
          <RefreshCw className={`w-5 h-5 ${uploading ? 'animate-spin' : ''}`} />
          {uploading ? 'Sincronizando...' : 'Sincronizar todos los archivos CVS manualmente'}
        </button>
      </div>
    </div>
  )
}
